/**
 * E002-T040: Database Rows Service
 *
 * Utilities for reading and processing Database rows for automation.
 * Uses the official DatabaseBlockDataSource API for clean architecture.
 *
 * - Read rows via DataSource API (not direct props access)
 * - Apply filters using Data-View's evalFilter
 * - Apply mappings to create payloads
 */

import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import { evalFilter, type FilterGroup } from '@blocksuite/affine/data-view';
import type { Doc, Store } from '@blocksuite/affine/store';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  isStatusTrackingPredicateConfigured,
  isStatusTrackingRowOpen,
} from '@streamlain/database-identity';

/**
 * Row data extracted from a Database block
 */
export interface DatabaseRow {
  /** Row ID (block ID) */
  id: string;
  /** Row ID alias for compatibility */
  rowId: string;
  /** Cell values keyed by column ID */
  cells: Record<string, unknown>;
}

/**
 * Result of reading and processing Database rows
 */
export interface ProcessedRows {
  /** Source table name */
  sourceTable: string;
  /** Source table ID */
  sourceTableId: string;
  /** All rows before filtering */
  allRows: DatabaseRow[];
  /** Rows that passed the filter */
  filteredRows: DatabaseRow[];
  /** Rows that were filtered out */
  filteredOutCount: number;
  /** Payloads ready to send to workflow */
  payloads: Array<Record<string, unknown>>;
  /** T048: Select column options for ID -> Label mapping (columnId -> Map<optionId, optionLabel>) */
  selectOptions?: Map<string, Map<string, string>>;
}

function resolveStore(doc: Doc | Store): Store {
  if (typeof (doc as Doc).getStore === 'function') {
    return (doc as Doc).getStore();
  }
  return doc as Store;
}

/**
 * Convert a cell value to a filter-friendly format using the property's toJson function.
 *
 * This matches how the Data-View's table-view-manager.ts does filtering:
 * it uses `column.cellGetOrCreate(rowId).jsonValue$.value` which internally
 * calls `propertyMeta.config.rawValue.toJson`.
 *
 * Using the same conversion ensures our filters work identically to the
 * native Data-View filters.
 */
function convertCellValueForFilter(
  dataSource: DatabaseBlockDataSource,
  propertyId: string,
  value: unknown
): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  const propertyType = dataSource.propertyTypeGet(propertyId);
  if (!propertyType) {
    return value;
  }

  // Use the official toJson conversion (same as jsonValue$ in data-view)
  const propertyMeta = dataSource.propertyMetaGet(propertyType);
  if (propertyMeta?.config?.rawValue?.toJson) {
    try {
      const jsonValue = propertyMeta.config.rawValue.toJson({
        value,
        data: dataSource.propertyDataGet(propertyId),
        dataSource,
      });
      return jsonValue;
    } catch (error) {
      console.warn(
        `[ConvertForFilter] Failed to convert ${propertyType} for ${propertyId}:`,
        error
      );
    }
  }

  // Fallback: Handle common types manually if toJson is not available
  // Handle Title/RichText: Y.Text objects have toString()
  if (propertyType === 'title' || propertyType === 'rich-text') {
    if (value && typeof value === 'object' && 'toString' in value) {
      return (value as { toString: () => string }).toString();
    }
    return String(value);
  }

  // Handle attachment: Convert file objects to file names (T005 fix)
  // Attachment raw value is Record<string, FileItemType> where FileItemType has { name, ... }
  // Filter expects string[] of file names
  if (propertyType === 'attachment') {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Raw format: { id1: { name: "file.jpg", ... }, id2: { ... } }
      const fileNames = Object.values(value as Record<string, unknown>)
        .filter(
          (item): item is { name: string } =>
            item !== null &&
            typeof item === 'object' &&
            'name' in item &&
            typeof (item as { name: unknown }).name === 'string'
        )
        .map(item => item.name);
      return fileNames;
    }
    if (Array.isArray(value)) {
      // Already converted to array (e.g., via toJson)
      if (value.every(v => typeof v === 'string')) {
        return value;
      }
      // Array of file objects
      const fileNames = value
        .filter(
          (item): item is { name: string } =>
            item !== null &&
            typeof item === 'object' &&
            'name' in item &&
            typeof (item as { name: unknown }).name === 'string'
        )
        .map(item => item.name);
      return fileNames;
    }
    return [];
  }

  // Handle any other object types - convert to string to be safe
  if (
    typeof value === 'object' &&
    value !== null &&
    'toString' in value &&
    typeof value.toString === 'function'
  ) {
    const str = value.toString();
    if (str !== '[object Object]') {
      return str;
    }
  }

  return value;
}

/**
 * Read all rows from a Database block using the official DataSource API
 */
export function readDatabaseRows(
  doc: Doc | Store,
  databaseBlockId: string
): DatabaseRow[] {
  const store = resolveStore(doc);
  const databaseBlock = store.getBlock(databaseBlockId);
  if (!databaseBlock) {
    console.warn(`[DatabaseRows] Database block not found: ${databaseBlockId}`);
    return [];
  }

  const model = databaseBlock.model as DatabaseBlockModel | undefined;
  if (!model) {
    console.warn(`[DatabaseRows] Database model not found: ${databaseBlockId}`);
    return [];
  }

  // Use the official DataSource API instead of direct props access
  const dataSource = createDatabaseBlockDataSource(model);

  const rowIds = dataSource.getBoundedRowsWindow().rowIds;

  // Get all property IDs (columns)
  const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;

  const rows: DatabaseRow[] = [];

  for (const rowId of rowIds) {
    const cellValues: Record<string, unknown> = {};

    // Use cellValueGet for each property - the official API
    // Convert values to filter-friendly format (e.g., Select option IDs -> labels)
    for (const propertyId of propertyIds) {
      const rawValue = dataSource.cellValueGet(rowId, propertyId);
      cellValues[propertyId] = convertCellValueForFilter(
        dataSource,
        propertyId,
        rawValue
      );
    }

    rows.push({
      id: rowId,
      rowId: rowId,
      cells: cellValues,
    });
  }

  return rows;
}

/**
 * Apply filters to rows using Data-View's evalFilter
 */
export function applyFilters(
  rows: DatabaseRow[],
  filterGroup: FilterGroup | null | undefined
): DatabaseRow[] {
  // No filter or empty filter group - return all rows
  if (!filterGroup || filterGroup.conditions.length === 0) {
    return rows;
  }

  console.log(
    '[ApplyFilters] Filter group:',
    JSON.stringify(filterGroup, null, 2)
  );
  console.log('[ApplyFilters] Total rows before filter:', rows.length);

  const filtered = rows.filter(row => {
    // evalFilter expects a Record<string, unknown> where keys match VariableRef.name
    // Our cells are keyed by column ID which should match
    const result = evalFilter(filterGroup, row.cells);
    console.log(
      '[ApplyFilters] Row',
      row.id,
      'evalFilter result:',
      result,
      'cells:',
      row.cells
    );
    return result;
  });

  console.log('[ApplyFilters] Rows after filter:', filtered.length);
  return filtered;
}

/**
 * Serialize attachment value as typed blob metadata for workflow execution.
 * Transforms raw attachment data to the format expected by workflow engine workflows
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function serializeAttachmentFile(
  value: unknown,
  fallbackBlobId?: string
): { blobId: string; name: string; mime: string; size: number } | null {
  if (!isPlainRecord(value)) {
    return null;
  }

  const blobId =
    nonEmptyString(value.sourceId) ??
    nonEmptyString(value.blobId) ??
    nonEmptyString(value.id) ??
    nonEmptyString(fallbackBlobId);

  if (!blobId) {
    return null;
  }

  const size =
    typeof value.size === 'number' && Number.isFinite(value.size)
      ? value.size
      : 0;

  return {
    blobId,
    name:
      nonEmptyString(value.name) ?? nonEmptyString(value.filename) ?? 'file',
    mime:
      nonEmptyString(value.mime) ??
      nonEmptyString(value.type) ??
      'application/octet-stream',
    size,
  };
}

function serializeAttachment(value: unknown): unknown {
  if (!isPlainRecord(value)) {
    return { _type: 'attachment', files: [] };
  }

  const filesValue = value.files;
  const files = (
    Array.isArray(filesValue)
      ? filesValue.map(file => serializeAttachmentFile(file))
      : Object.entries(value).map(([fileId, file]) =>
          serializeAttachmentFile(file, fileId)
        )
  ).filter((file): file is NonNullable<typeof file> => file !== null);

  return {
    _type: 'attachment',
    files,
  };
}

/**
 * Serialize Select/MultiSelect values with both ID and label for workflow engine workflows.
 * This allows workflow engine to display meaningful labels while keeping IDs for writebacks.
 */
function serializeSelectValue(
  dataSource: DatabaseBlockDataSource,
  propertyId: string,
  value: unknown,
  isMultiSelect: boolean
): unknown {
  const propertyData = dataSource.propertyDataGet(propertyId) as
    | { options?: Array<{ id: string; value: string; color?: string }> }
    | undefined;
  const options = propertyData?.options ?? [];

  // Build ID → label lookup
  const optionMap = new Map<string, string>();
  for (const opt of options) {
    if (opt.id && opt.value) {
      optionMap.set(opt.id, opt.value);
    }
  }

  if (isMultiSelect) {
    // Multi-select: value is string[] of option IDs
    const ids = Array.isArray(value) ? value : [];
    return ids.map(id => ({
      id,
      label: optionMap.get(id) ?? id,
    }));
  } else {
    // Single select: value is string (option ID) or null
    if (typeof value === 'string' && value) {
      return {
        id: value,
        label: optionMap.get(value) ?? value,
      };
    }
    return null;
  }
}

/**
 * Serialize Relation values with row IDs and titles.
 * Relations link to rows in other tables - include both ID and display value.
 */
function serializeRelationValue(value: unknown): unknown {
  // Relation value is typically an array of row IDs or objects
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(item => {
      if (typeof item === 'string') {
        // Just row ID - we don't have access to the title here
        return { rowId: item };
      }
      if (typeof item === 'object' && item !== null) {
        // Already structured
        return item;
      }
      return { rowId: String(item) };
    });
  }

  return [];
}

/**
 * Serialize a cell value for JSON payload using the PropertyMeta's toJson function
 * This converts internal BlockSuite formats (Y.Text, etc.) to plain JSON values
 *
 * Special handling for:
 * - Select/MultiSelect: Returns {id, label} objects so workflow engine can display labels
 * - Attachment: Returns typed blob metadata consumed through the service gateway
 * - Relation: Returns row references
 */
function serializeCellValue(
  dataSource: DatabaseBlockDataSource,
  propertyId: string,
  value: unknown
): unknown {
  // Skip null/undefined
  if (value === null || value === undefined) {
    return value;
  }

  // Get the property type (e.g., 'rich-text', 'number', 'text', 'select', etc.)
  const propertyType = dataSource.propertyTypeGet(propertyId);
  if (!propertyType) {
    console.warn(`[Serialize] Unknown property type for column ${propertyId}`);
    return value;
  }

  // Special handling for attachments - typed blob metadata only
  if (propertyType === 'attachment') {
    return serializeAttachment(value);
  }

  // Special handling for Select - include label for workflow engine display
  if (propertyType === 'select') {
    return serializeSelectValue(dataSource, propertyId, value, false);
  }

  // Special handling for Multi-Select - include labels for workflow engine display
  if (propertyType === 'multi-select') {
    return serializeSelectValue(dataSource, propertyId, value, true);
  }

  // Special handling for Relation columns
  if (propertyType === 'relation') {
    return serializeRelationValue(value);
  }

  // Get the property meta configuration
  const propertyMeta = dataSource.propertyMetaGet(propertyType);
  if (!propertyMeta) {
    console.warn(`[Serialize] No meta found for property type ${propertyType}`);
    return value;
  }

  const toJson = propertyMeta.config.rawValue.toJson;
  if (!toJson) {
    // No conversion needed, use value as-is
    return value;
  }

  try {
    // Convert the value using toJson
    // This handles conversions like Y.Text -> string
    const serialized = toJson({
      value,
      data: dataSource.propertyDataGet(propertyId),
      dataSource,
    });

    return serialized;
  } catch (error) {
    console.warn(
      `[Serialize] Failed to serialize ${propertyType} for ${propertyId}:`,
      error
    );
    // Fallback: try toString
    if (value && typeof value === 'object' && 'toString' in value) {
      return (value as { toString: () => string }).toString();
    }
    return value;
  }
}

/**
 * Build payloads from rows using selected columns
 *
 * Only includes the columns that were explicitly selected by the user.
 * If no columns are selected, includes all columns.
 * Values are serialized to JSON-safe format using PropertyMeta.toJson.
 */
export function buildPayloads(
  rows: DatabaseRow[],
  selectedColumns: string[],
  dataSource: DatabaseBlockDataSource,
  options?: {
    /** Use column names instead of IDs for keys */
    useColumnNames?: boolean;
    /** Schema-based mapping: columnId → schemaFieldId rename map */
    schemaFieldMap?: Map<string, string>;
  }
): Array<Record<string, unknown>> {
  return rows.map(row => {
    const payload: Record<string, unknown> = {
      // Always include row ID for tracking
      _rowId: row.id,
    };

    // If no columns selected, include all columns
    const columnsToInclude =
      selectedColumns.length > 0 ? selectedColumns : Object.keys(row.cells);

    for (const columnId of columnsToInclude) {
      if (columnId in row.cells) {
        // Get the key to use:
        // 1. Schema field name (if schema mapping active)
        // 2. Column name (default)
        // 3. Column ID (fallback)
        let key = columnId;
        const schemaField = options?.schemaFieldMap?.get(columnId);
        if (schemaField) {
          key = schemaField;
        } else if (options?.useColumnNames) {
          const columnName = dataSource.propertyNameGet(columnId);
          if (columnName) {
            key = columnName;
          }
        }

        // Serialize the value (convert Y.Text to string, etc.)
        const rawValue = row.cells[columnId];
        const serializedValue = serializeCellValue(
          dataSource,
          columnId,
          rawValue
        );
        payload[key] = serializedValue;
      }
    }

    return payload;
  });
}

/**
 * Input mapping: maps a schema field to a source column
 * (Duplicated here to avoid circular import from automation.ts)
 */
interface InputMappingRef {
  schemaFieldId: string;
  columnId: string | null;
}

/**
 * Process all rows for automation execution
 *
 * Uses the official DatabaseBlockDataSource API for reading rows,
 * and Data-View's FilterGroup for filtering.
 * Values are serialized to JSON-safe format.
 */
export function processRowsForExecution(
  doc: Doc | Store,
  databaseBlockId: string,
  databaseTitle: string,
  filterGroup: FilterGroup | null | undefined,
  selectedColumns: string[],
  options?: {
    /** Use column names instead of IDs for keys */
    useColumnNames?: boolean;
    /** E018-WF1: Input mappings (schema-based mode) - takes precedence over selectedColumns */
    inputMappings?: InputMappingRef[];
    /** E018-T006: Status tracking config for auto-filter */
    statusTracking?: {
      enabled: boolean;
      mode?: 'status_column' | 'delete_row';
      columnId: string | null;
      successValue: string;
    };
  }
): ProcessedRows {
  const store = resolveStore(doc);

  // Get the database block and create DataSource
  const databaseBlock = store.getBlock(databaseBlockId);
  if (!databaseBlock) {
    console.warn(`[ProcessRows] Database block not found: ${databaseBlockId}`);
    return {
      sourceTable: databaseTitle,
      sourceTableId: databaseBlockId,
      allRows: [],
      filteredRows: [],
      filteredOutCount: 0,
      payloads: [],
    };
  }

  const model = databaseBlock.model as DatabaseBlockModel | undefined;
  if (!model) {
    console.warn(`[ProcessRows] Database model not found: ${databaseBlockId}`);
    return {
      sourceTable: databaseTitle,
      sourceTableId: databaseBlockId,
      allRows: [],
      filteredRows: [],
      filteredOutCount: 0,
      payloads: [],
    };
  }

  const dataSource = createDatabaseBlockDataSource(model);

  // Read all rows using the official DataSource API
  const allRows = readDatabaseRows(doc, databaseBlockId);

  // Apply filters using Data-View's evalFilter
  let filteredRows = applyFilters(allRows, filterGroup);

  // T048: Build select options map for ID -> Label mapping (needed for status tracking filter)
  const selectOptions = new Map<string, Map<string, string>>();
  const propertyIds = dataSource.getBoundedPropertyIdsWindow().propertyIds;
  for (const propertyId of propertyIds) {
    const propertyType = dataSource.propertyTypeGet(propertyId);
    if (propertyType === 'select' || propertyType === 'multi-select') {
      const propertyData = dataSource.propertyDataGet(propertyId) as
        | { options?: Array<{ id: string; value: string }> }
        | undefined;
      if (propertyData?.options) {
        const optionMap = new Map<string, string>();
        for (const opt of propertyData.options) {
          if (opt.id && opt.value) {
            optionMap.set(opt.id, opt.value);
          }
        }
        if (optionMap.size > 0) {
          selectOptions.set(propertyId, optionMap);
        }
      }
    }
  }

  // E018-T006: Apply status tracking auto-filter (skip already processed rows)
  const statusTracking = options?.statusTracking;
  if (statusTracking?.enabled && statusTracking.mode !== 'delete_row') {
    const beforeCount = filteredRows.length;
    if (isStatusTrackingPredicateConfigured(statusTracking)) {
      const statusColumnOptions = selectOptions.get(statusTracking.columnId);

      filteredRows = filteredRows.filter(row =>
        isStatusTrackingRowOpen(
          statusTracking,
          row.cells[statusTracking.columnId],
          statusColumnOptions
        )
      );
    } else {
      console.warn(
        `[ProcessRows] Status tracking enabled but missing columnId or successValue for ${databaseBlockId}`
      );
    }

    const statusFilteredOut = beforeCount - filteredRows.length;
    if (statusFilteredOut > 0) {
      console.debug(
        `[ProcessRows] StatusTracking skipped ${statusFilteredOut} rows with status "${statusTracking.successValue}"`
      );
    }
  }

  // E018-WF1: Determine which columns to include
  // Priority: inputMappings > selectedColumns > all columns
  let columnsToInclude = selectedColumns;
  // Schema-based mapping: build columnId → schemaFieldId rename map
  let schemaFieldMap: Map<string, string> | undefined;
  if (options?.inputMappings && options.inputMappings.length > 0) {
    // Extract columnIds from inputMappings (filter out null/unmapped)
    const mapped = options.inputMappings.filter(m => m.columnId !== null);
    columnsToInclude = mapped.map(m => m.columnId as string);
    // When schema mapping is active, use schemaFieldId as payload keys
    schemaFieldMap = new Map(
      mapped.map(m => [m.columnId as string, m.schemaFieldId])
    );
  }

  // Build payloads with selected columns only (serialized to JSON)
  const payloads = buildPayloads(filteredRows, columnsToInclude, dataSource, {
    useColumnNames: options?.useColumnNames ?? true, // Default to using column names
    schemaFieldMap, // Schema-based: rename columns to schema field names
  });

  // selectOptions map was already built above (for status tracking filter)

  return {
    sourceTable: databaseTitle,
    sourceTableId: databaseBlockId,
    allRows,
    filteredRows,
    filteredOutCount: allRows.length - filteredRows.length,
    payloads,
    selectOptions,
  };
}

/**
 * S020-8.20.2: Relation match mode for Smart Relation Lookup
 */
/**
 * Output mapping: maps a response field to a target column.
 * sl-qi4j.8: Removed fields (relationLookup, relationMatchMode) replaced by matchConfigId.
 */
export interface OutputMapping {
  responseField: string;
  targetColumnId: string;
  /** sl-qi4j.8: Reference to MatchConfig entry for relation lookup or row matching. */
  matchConfigId?: string;
  /** sl-vb5r: Fixed value (option ID) for select columns — mutually exclusive with responseField */
  fixedValue?: string;
}

/**
 * Get a nested value from an object using dot notation path
 * e.g. "data.name" from { data: { name: "John" } } => "John"
 */
function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    // Handle array access like "items[0]"
    const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, key, indexStr] = arrayMatch;
      const index = parseInt(indexStr, 10);
      if (current && typeof current === 'object' && key in current) {
        const arr = (current as Record<string, unknown>)[key];
        if (Array.isArray(arr)) {
          current = arr[index];
          continue;
        }
      }
      return undefined;
    }

    if (current && typeof current === 'object' && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Convert select/multi-select labels to option IDs.
 * workflow engine workflows typically send labels (strings), but the database expects IDs.
 */
function castSelectValue(
  dataSource: DatabaseBlockDataSource,
  propertyId: string,
  value: unknown,
  isMultiSelect: boolean
): unknown {
  const propertyData = dataSource.propertyDataGet(propertyId) as
    | { options?: Array<{ id: string; value: string }> }
    | undefined;
  const options = propertyData?.options ?? [];

  // Build label → ID lookup (case-insensitive)
  const labelToId = new Map<string, string>();
  const idSet = new Set<string>();
  for (const opt of options) {
    if (opt.id && opt.value) {
      labelToId.set(opt.value.toLowerCase(), opt.id);
      idSet.add(opt.id);
    }
  }

  /**
   * Resolve a single value (could be ID, label, or {id, label} object)
   */
  const resolveValue = (v: unknown): string | null => {
    if (typeof v === 'string') {
      // Check if it's already an ID
      if (idSet.has(v)) return v;
      // Try to find by label (case-insensitive)
      return labelToId.get(v.toLowerCase()) ?? null;
    }
    if (typeof v === 'object' && v !== null) {
      // Could be {id, label} object from our serialization
      const obj = v as { id?: string; label?: string };
      if (obj.id && idSet.has(obj.id)) return obj.id;
      if (obj.label) return labelToId.get(obj.label.toLowerCase()) ?? null;
    }
    return null;
  };

  if (isMultiSelect) {
    // Multi-select expects string[] of option IDs
    if (!Array.isArray(value)) {
      // Single value - wrap in array
      const resolved = resolveValue(value);
      return resolved ? [resolved] : [];
    }
    const ids = value
      .map(resolveValue)
      .filter((id): id is string => id !== null);
    console.log(
      `[TypeCast] multi-select: ${JSON.stringify(value)} -> ${JSON.stringify(ids)}`
    );
    return ids;
  } else {
    // Single select expects string (option ID) or null
    const resolved = resolveValue(value);
    console.log(`[TypeCast] select: ${JSON.stringify(value)} -> ${resolved}`);
    return resolved;
  }
}

/**
 * Convert a raw value to the correct type for a database column.
 *
 * Uses the property's fromJson function to convert primitives (string, number, etc.)
 * to the correct internal format (e.g., string -> Text for rich-text columns).
 *
 * Special handling for:
 * - Select/MultiSelect: Converts labels to option IDs
 */
function castValueForColumn(
  dataSource: DatabaseBlockDataSource,
  propertyId: string,
  value: unknown
): unknown {
  // Get the property type (e.g., 'rich-text', 'number', 'text', 'select', etc.)
  const propertyType = dataSource.propertyTypeGet(propertyId);
  if (!propertyType) {
    throw new Error(`Unknown property type for column ${propertyId}`);
  }

  if (propertyType === 'relation') {
    throw new Error(
      `Relation column ${propertyId} requires RelationWriteService`
    );
  }

  // Special handling for Select - convert labels to IDs
  if (propertyType === 'select') {
    return castSelectValue(dataSource, propertyId, value, false);
  }

  // Special handling for Multi-Select - convert labels to IDs
  if (propertyType === 'multi-select') {
    return castSelectValue(dataSource, propertyId, value, true);
  }

  // Get the property meta configuration
  const propertyMeta = dataSource.propertyMetaGet(propertyType);
  if (!propertyMeta) {
    throw new Error(`No meta found for property type ${propertyType}`);
  }

  const fromJson = propertyMeta.config.rawValue.fromJson;
  if (!fromJson) {
    // No conversion needed, use value as-is
    return value;
  }

  try {
    // Convert the value using fromJson
    // This handles conversions like string -> Text for rich-text columns
    const converted = fromJson({
      value,
      data: dataSource.propertyDataGet(propertyId),
      dataSource,
    });

    console.log(
      `[TypeCast] Converted ${propertyType}: "${value}" -> `,
      converted
    );
    return converted;
  } catch (error) {
    throw new Error(`Failed to convert value for ${propertyType}: ${error}`);
  }
}

/**
 * Result of validating an output mapping (dry-run)
 */
export interface MappingValidationResult {
  /** Whether all mappings would succeed */
  success: boolean;
  /** Details for each mapping */
  mappings: Array<{
    responseField: string;
    targetColumnId: string;
    targetColumnName?: string;
    /** Raw value extracted from response */
    rawValue: unknown;
    /** Value after type casting */
    castedValue: unknown;
    /** Whether this mapping would succeed */
    valid: boolean;
    /** Error message if invalid */
    error?: string;
  }>;
  /** Overall errors (e.g., table not found) */
  errors: string[];
}

/**
 * Validate output mappings without actually writing (dry-run)
 *
 * Used by Test Run to verify that mappings would work
 * without modifying any data.
 */
export function validateOutputMapping(
  doc: Doc | Store,
  targetTableId: string,
  response: unknown,
  mappings: OutputMapping[]
): MappingValidationResult {
  const result: MappingValidationResult = {
    success: true,
    mappings: [],
    errors: [],
  };

  if (!mappings || mappings.length === 0) {
    return result;
  }

  const store = resolveStore(doc);
  const databaseBlock = store.getBlock(targetTableId);
  if (!databaseBlock) {
    return {
      success: false,
      mappings: [],
      errors: [`Database block not found: ${targetTableId}`],
    };
  }

  const model = databaseBlock.model as DatabaseBlockModel | undefined;
  if (!model) {
    return {
      success: false,
      mappings: [],
      errors: [`Database model not found: ${targetTableId}`],
    };
  }

  // Use the official DataSource API
  const dataSource = createDatabaseBlockDataSource(model);

  for (const mapping of mappings) {
    const mappingResult: MappingValidationResult['mappings'][0] = {
      responseField: mapping.responseField,
      targetColumnId: mapping.targetColumnId,
      targetColumnName: dataSource.propertyNameGet(mapping.targetColumnId),
      rawValue: undefined,
      castedValue: undefined,
      valid: false,
    };

    try {
      // Extract value from response using the field path
      const rawValue = getNestedValue(response, mapping.responseField);
      mappingResult.rawValue = rawValue;

      if (rawValue === undefined) {
        mappingResult.error = `Field "${mapping.responseField}" not found in response`;
        result.success = false;
        result.mappings.push(mappingResult);
        continue;
      }

      // Try to convert the value to the correct type for the target column
      const castedValue = castValueForColumn(
        dataSource,
        mapping.targetColumnId,
        rawValue
      );
      mappingResult.castedValue = castedValue;
      mappingResult.valid = true;

      console.log(
        `[Validate] Mapping "${mapping.responseField}" -> "${mappingResult.targetColumnName}": ${rawValue} -> `,
        castedValue
      );
    } catch (error) {
      mappingResult.error = `Type conversion failed: ${error}`;
      result.success = false;
    }

    result.mappings.push(mappingResult);
  }

  return result;
}

/**
 * Write response data back to the source table based on mappings
 *
 * This is called after a successful execution to update the source row
 * with data from the workflow engine response.
 */
export function writebackToSourceRow(
  doc: Doc | Store,
  sourceTableId: string,
  rowId: string,
  response: unknown,
  mappings: OutputMapping[]
): { success: boolean; updatedFields: string[]; errors: string[] } {
  const errors: string[] = [];
  const updatedFields: string[] = [];

  if (!mappings || mappings.length === 0) {
    return { success: true, updatedFields: [], errors: [] };
  }

  const store = resolveStore(doc);
  const databaseBlock = store.getBlock(sourceTableId);
  if (!databaseBlock) {
    return {
      success: false,
      updatedFields: [],
      errors: [`Database block not found: ${sourceTableId}`],
    };
  }

  const model = databaseBlock.model as DatabaseBlockModel | undefined;
  if (!model) {
    return {
      success: false,
      updatedFields: [],
      errors: [`Database model not found: ${sourceTableId}`],
    };
  }

  // Use the official DataSource API
  const dataSource = createDatabaseBlockDataSource(model);
  const validation = validateOutputMapping(
    store,
    sourceTableId,
    response,
    mappings
  );
  if (!validation.success) {
    return {
      success: false,
      updatedFields: [],
      errors: validation.mappings
        .filter(mapping => !!mapping.error)
        .map(
          mapping =>
            `${mapping.responseField} -> ${mapping.targetColumnId}: ${mapping.error}`
        ),
    };
  }

  for (const mapping of validation.mappings) {
    try {
      // Write to the target column using the DataSource API
      dataSource.cellValueChange(
        rowId,
        mapping.targetColumnId,
        mapping.castedValue
      );
      updatedFields.push(mapping.targetColumnId);

      console.log(
        `[Writeback] Updated ${mapping.targetColumnId} with value from ${mapping.responseField}:`,
        mapping.castedValue
      );
    } catch (error) {
      const errorMsg = `Failed to write ${mapping.responseField} to ${mapping.targetColumnId}: ${error}`;
      console.error(`[Writeback] ${errorMsg}`);
      errors.push(errorMsg);
    }
  }

  return {
    success: errors.length === 0,
    updatedFields,
    errors,
  };
}

/**
 * Append a new row to the target table based on mappings
 *
 * This is called after a successful execution in "append" mode
 * to create a new row in the target table with data from the workflow engine response.
 */
export function appendToTargetTable(
  doc: Doc | Store,
  targetTableId: string,
  response: unknown,
  mappings: OutputMapping[]
): {
  success: boolean;
  newRowId?: string;
  updatedFields: string[];
  errors: string[];
} {
  const errors: string[] = [];
  const updatedFields: string[] = [];

  if (!mappings || mappings.length === 0) {
    return { success: true, updatedFields: [], errors: [] };
  }

  const store = resolveStore(doc);
  const databaseBlock = store.getBlock(targetTableId);
  if (!databaseBlock) {
    return {
      success: false,
      updatedFields: [],
      errors: [`Target database block not found: ${targetTableId}`],
    };
  }

  const model = databaseBlock.model as DatabaseBlockModel | undefined;
  if (!model) {
    return {
      success: false,
      updatedFields: [],
      errors: [`Target database model not found: ${targetTableId}`],
    };
  }

  // Use the official DataSource API
  const dataSource = createDatabaseBlockDataSource(model);
  const validation = validateOutputMapping(
    store,
    targetTableId,
    response,
    mappings
  );
  if (!validation.success) {
    return {
      success: false,
      updatedFields: [],
      errors: validation.mappings
        .filter(mapping => !!mapping.error)
        .map(
          mapping =>
            `${mapping.responseField} -> ${mapping.targetColumnId}: ${mapping.error}`
        ),
    };
  }

  // Add a new row at the end
  const newRowId = dataSource.rowAdd('end');
  console.log(`[Writeback] Created new row: ${newRowId}`);

  // Set values for each mapping
  for (const mapping of validation.mappings) {
    try {
      // Write to the target column using the DataSource API
      dataSource.cellValueChange(
        newRowId,
        mapping.targetColumnId,
        mapping.castedValue
      );
      updatedFields.push(mapping.targetColumnId);

      console.log(
        `[Writeback] Set ${mapping.targetColumnId} = ${mapping.responseField}:`,
        mapping.castedValue
      );
    } catch (error) {
      const errorMsg = `Failed to write ${mapping.responseField} to ${mapping.targetColumnId}: ${error}`;
      console.error(`[Writeback] ${errorMsg}`);
      errors.push(errorMsg);
    }
  }

  return {
    success: errors.length === 0,
    newRowId,
    updatedFields,
    errors,
  };
}
